import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";

const ContactRow = ({ c, onChange, isAdmin, eid, onChanged }) => {
  const except = async () => {
    const reason = window.prompt(`Admin exception for ${c.relation} contact — reason (saved in audit log)`);
    if (!reason?.trim()) return;
    try {
      await api.post(`/hr/employees/${eid}/exceptions`, { item_key: `family:${c.relation}`, reason });
      toast.success("Exception recorded"); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center py-2 border-b border-border last:border-0" data-testid={`hr-family-${c.relation}`}>
      <div className="md:col-span-2 font-medium capitalize text-[13px]">
        {c.relation} *
        <div className={`text-[10px] uppercase tracking-wider ${c.complete || c.excepted ? "text-green-700" : "text-red-600"}`}>
          {c.complete ? "Complete" : c.excepted ? "Admin exception" : "Missing"}
        </div>
      </div>
      <Input className="md:col-span-3 h-9" placeholder="Full name" value={c.name} disabled={c.unavailable}
        onChange={(e) => onChange({ name: e.target.value })} data-testid={`hr-family-name-${c.relation}`} />
      <Input className="md:col-span-2 h-9 font-mono" placeholder="10-digit mobile" value={c.mobile} disabled={c.unavailable}
        inputMode="numeric" onChange={(e) => onChange({ mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} data-testid={`hr-family-mobile-${c.relation}`} />
      <label className="md:col-span-2 flex items-center gap-2 text-[12px]">
        <input type="checkbox" checked={c.unavailable} onChange={(e) => onChange({ unavailable: e.target.checked })} data-testid={`hr-family-unavail-${c.relation}`} />
        Unavailable
      </label>
      <div className="md:col-span-3 flex items-center gap-1">
        {c.unavailable && (
          <Input className="h-9" placeholder="Reason (required)" value={c.reason}
            onChange={(e) => onChange({ reason: e.target.value })} data-testid={`hr-family-reason-${c.relation}`} />
        )}
        {isAdmin && !c.complete && !c.excepted && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-amber-700" title="Admin exception" onClick={except} data-testid={`hr-family-except-${c.relation}`}>
            <ShieldCheck size={14} />
          </Button>
        )}
      </div>
    </div>
  );
};

export const FamilyContacts = ({ eid, family, isAdmin, onChanged }) => {
  const [rows, setRows] = useState(family);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRows(family); }, [family]);

  const save = async () => {
    for (const r of rows) {
      if (r.unavailable && !r.reason.trim()) { toast.error(`${r.relation}: reason is required when unavailable`); return; }
      if (!r.unavailable && r.mobile && r.mobile.length !== 10) { toast.error(`${r.relation}: mobile must be 10 digits`); return; }
    }
    setSaving(true);
    try {
      await api.put(`/hr/employees/${eid}/family`, {
        contacts: rows.map(({ relation, name, mobile, unavailable, reason }) => ({ relation, name, mobile, unavailable, reason })),
      });
      toast.success("Family contacts saved"); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="card-flat p-4 space-y-1" data-testid="hr-family-section">
      {rows.map((c, i) => (
        <ContactRow key={c.relation} c={c} isAdmin={isAdmin} eid={eid} onChanged={onChanged}
          onChange={(patch) => setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)))} />
      ))}
      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="h-9" data-testid="hr-family-save">Save Family Contacts</Button>
      </div>
    </div>
  );
};
